/**
 * Feature 1 — Public Vendor Profile (`/v/:slug`).
 *
 * Layer 1 (anon) and Layer 2 (auth) rendered from the same page —
 * server decides which payload to return based on Authorization header.
 *
 * The DevelopingProfileState (from F3) is reused when the publish gate
 * hasn't been met yet.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { useAuthStore } from '../stores/authStore';
import VendorScoreCard from '../components/profile/VendorScoreCard';
import DevelopingProfileState from '../components/DevelopingProfileState';
import LoadingSpinner from '../components/LoadingSpinner';
import UnclaimedCompanyProfile from '../components/UnclaimedCompanyProfile';
import ScoreDisclaimer from '../components/ScoreDisclaimer';
import { publicProfileApi } from '../services/publicProfile';
import type { PublicProfileResponse } from '../types';


// ── Helpers ─────────────────────────────────────────────────────────────────

function ParticipationBadge({ status }: { status: string }) {
  const variant =
    status === 'Active Participant'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'Unclaimed'
      ? 'bg-slate-100 text-slate-700 border-slate-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${variant}`}>
      {status}
    </span>
  );
}


// ── Blurred Layer 2 teaser ─────────────────────────────────────────────────


/**
 * Stub of a Layer 2 section shown to anon viewers.  The section title
 * is fully visible so the buyer knows what's behind the wall; the
 * content under it is fake text + heavily blurred + overlaid with a
 * "Sign up to view" badge.  Goal: create curiosity without breaking
 * the spec's data-exposure rules.
 */
function BlurredTeaser({
  title, slug, lines = 0, chart = false,
}: { title: string; slug: string; lines?: number; chart?: boolean }) {
  // Fake filler text — never derived from real data.  Random lengths
  // so it looks like a paragraph rather than a uniform block.
  const FILLER = [
    'Lorem ipsum dolor sit amet consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat duis aute irure dolor.',
    'Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum nec consectetur.',
    'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua enim ad minim veniam quis nostrud exercitation ullamco.',
    'Dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua minim veniam.',
  ];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 relative overflow-hidden">
      <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
        {title}
      </div>

      {/* Blurred filler content.  pointer-events-none so the click
          area belongs entirely to the overlay's signup link. */}
      <div className="select-none pointer-events-none filter blur-sm space-y-2" aria-hidden="true">
        {Array.from({ length: lines }).map((_, i) => (
          <p key={i} className="text-slate-700">
            {FILLER[i % FILLER.length]}
          </p>
        ))}
        {chart && (
          <div className="mt-3 flex items-end gap-1 h-16">
            {Array.from({ length: 18 }).map((_, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm bg-brand-400"
                style={{
                  height: `${30 + ((i * 13) % 60)}%`,
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Overlay — translucent gradient + signup CTA */}
      <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-white via-white/80 to-transparent">
        <div className="mb-4 flex flex-col items-center gap-2 text-center">
          <span aria-hidden className="text-2xl">&#128274;</span>
          <Link
            to={`/signup?return=/v/${slug}`}
            className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-brand-600"
          >
            Sign up to view {title.toLowerCase()}
          </Link>
        </div>
      </div>
    </div>
  );
}


// (VerificationRequestModal was removed — the CTA was hidden after the
//  spec-locked verification levels system was cut.  Restore from git
//  history if/when the admin review side gets built.)


// ── Page ───────────────────────────────────────────────────────────────────

export default function PublicVendorProfile() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  // VerificationRequestModal kept defined (line ~118) so we can re-enable
  // the CTA when the admin review side gets built.  Until then, the
  // button is hidden from the page.

  const { data, isLoading, isError } = useQuery<PublicProfileResponse>({
    queryKey: ['public-profile', slug],
    queryFn: () => publicProfileApi.getProfile(slug!),
    enabled: !!slug,
    retry: false,
  });

  // Fire view tracking once on mount (after the profile loads).
  useEffect(() => {
    if (!slug || !data) return;
    if ('is_developing_profile' in data && data.is_developing_profile) return;
    if ('is_unclaimed' in data && data.is_unclaimed) return;
    publicProfileApi.recordView(slug).catch(() => {
      // Throttled / network error — analytics best-effort.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, data && 'layer' in data ? data.layer : null]);

  // Set SEO meta tags on mount, restore on unmount.
  useEffect(() => {
    if (!data) return;
    if ('is_developing_profile' in data && data.is_developing_profile) return;
    if ('is_unclaimed' in data && data.is_unclaimed) return;
    if (!('vendor' in data)) return;
    const v = data.vendor;
    const oldTitle = document.title;
    document.title = `${v.name} — GuardGrade Profile`;
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    const oldContent = meta.content;
    const summary = 'accountability_summary' in v ? v.accountability_summary : '';
    meta.content = (summary || '').split('. ')[0] + '.';
    return () => {
      document.title = oldTitle;
      if (meta) {
        if (created) meta.remove();
        else meta.content = oldContent;
      }
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-2xl py-24 text-center text-slate-600">
        Profile not found.
      </div>
    );
  }

  // Feature 14 — unclaimed PublicCompany branch.
  if ('is_unclaimed' in data && data.is_unclaimed) {
    return (
      <UnclaimedCompanyProfile
        company={data.company}
        canClaim={data.ctas.can_claim_profile}
      />
    );
  }

  if ('is_developing_profile' in data && data.is_developing_profile) {
    return (
      <DevelopingProfileState
        vendorName={data.vendor.name}
        city={data.vendor.city}
        state={data.vendor.state}
      />
    );
  }

  // Narrow down: after the two early returns above, data must be one of the
  // claimed-profile shapes.  TypeScript's structural narrowing doesn't
  // flow through `in` checks here, so we re-cast.
  if (!('layer' in data)) {
    return null;
  }
  const v = data.vendor;
  const isLayer2 = data.layer === 2;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-12">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-semibold text-slate-900">{v.name}</h1>
            <ParticipationBadge status={v.participation_status} />
          </div>
          {(v.city || v.state) && (
            <div className="mt-1 text-sm text-slate-500">
              {[v.city, v.state].filter(Boolean).join(', ')}
            </div>
          )}
        </div>

        {/* Score card — extracted to a presentational component so it
            can be embedded on marketing surfaces without dragging the
            full-page layout along (see VendorScoreCard.tsx). */}
        <div className="mb-6">
          <VendorScoreCard
            score={v.score}
            statusLabel={v.status_label}
            trustStack={v.trust_stack}
          />
        </div>

        {/* Accountability summary */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-6">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
            Accountability summary
          </div>
          <p className="text-slate-700">{v.accountability_summary}</p>
          <dl className="mt-4 grid grid-cols-3 gap-3 text-sm">
            <div>
              <dt className="text-xs text-slate-400">Sites</dt>
              <dd className="font-semibold text-slate-700">
                {v.operational_scope.site_count}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-400">Guards</dt>
              <dd className="font-semibold text-slate-700">
                {v.operational_scope.guard_count}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-400">Regions</dt>
              <dd className="font-semibold text-slate-700">
                {v.operational_scope.regions.length > 0
                  ? v.operational_scope.regions.join(', ')
                  : '—'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Layer 2 — score explanation + trend + methodology.  Anon
            viewers see blurred teaser stubs of these same sections so
            they can feel what's behind the signup wall. */}
        {isLayer2 && 'score_explanation' in v ? (
          <>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-6">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                Score explanation
              </div>
              <p className="text-slate-700">{v.score_explanation}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-6">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                Trend
              </div>
              <p className="text-slate-700">{v.trend_summary}</p>
              {v.trend_snapshots.length >= 2 && (
                <div className="mt-4">
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart
                      data={v.trend_snapshots.map((s: { week_start: string; score: number }) => ({
                        week: new Date(s.week_start + 'T00:00:00').toLocaleDateString(
                          'en-US', { month: 'short', day: 'numeric' },
                        ),
                        score: s.score,
                      }))}
                      margin={{ top: 10, right: 16, left: -10, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="public-trend-fill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="week"
                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e2e8f0' }}
                      />
                      <YAxis
                        domain={[300, 900]}
                        ticks={[300, 450, 600, 750, 900]}
                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <ReferenceLine
                        y={750}
                        stroke="#10b981"
                        strokeDasharray="3 3"
                        label={{ value: 'Strong', position: 'right', fontSize: 10, fill: '#10b981' }}
                      />
                      <ReferenceLine
                        y={650}
                        stroke="#f59e0b"
                        strokeDasharray="3 3"
                        label={{ value: 'Stable', position: 'right', fontSize: 10, fill: '#f59e0b' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#f8fafc',
                          fontSize: '12px',
                        }}
                        formatter={(value) => [`${value} / 900`, 'GuardGrade Score']}
                        labelFormatter={(label) => `Week of ${label}`}
                      />
                      <Area
                        type="monotone"
                        dataKey="score"
                        stroke="#10b981"
                        strokeWidth={2}
                        fill="url(#public-trend-fill)"
                        dot={{ r: 3, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
                        activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-6">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                Methodology
              </div>
              <p className="text-sm text-slate-600">{v.methodology_context}</p>
            </div>
          </>
        ) : (
          <>
            {/* Anon teasers — blurred placeholders for the Layer 2
                sections.  Each shows the section's title in the open
                so the viewer knows what's behind the wall, and the
                fake content underneath is heavily blurred. */}
            <BlurredTeaser
              title="Score explanation"
              slug={v.profile_slug}
              lines={3}
            />
            <BlurredTeaser
              title="Score trend"
              slug={v.profile_slug}
              chart
            />
            <BlurredTeaser
              title="Methodology"
              slug={v.profile_slug}
              lines={4}
            />
          </>
        )}

        {/* CTAs */}
        <div className="flex flex-wrap gap-3">
          {!user && (
            <Link
              to={`/signup?return=/v/${v.profile_slug}`}
              className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
            >
              Sign up to view full report
            </Link>
          )}
          {/* "Request verification access" hidden — V1 cut.  The form
              still exists at /api/public/v/<slug>/verification-request/
              for future use but the visible CTA is gone since the
              admin review side wasn't built. */}
        </div>

        {/* Feature 10: full disclaimer footer on every public profile page */}
        <div className="mt-12 border-t border-slate-200 pt-6">
          <ScoreDisclaimer variant="full" />
        </div>
      </div>
    </div>
  );
}
